// Introspect Page Types

export interface ColumnStatistics {
  name: string;
  type: string;
  null_count: number;
  unique_count: number;
  min?: number | string | null;
  max?: number | string | null;
  mean?: number | null;
}

export interface DataQuality {
  overall_score: number;
  null_percentage: number;
  duplicate_percentage: number;
  issues: string[];
}

export interface StatisticalSummary {
  row_count: number;
  column_count: number;
  columns: ColumnStatistics[];
}

export interface BusinessInsights {
  summary: string;
  recommendations: string[];
  potential_use_cases: string[];
}

export interface InsightsData {
  file_key: string;
  generated_at: string;
  cached: boolean;
  data_quality: DataQuality;
  statistical_summary: StatisticalSummary;
  business_insights: BusinessInsights;
}

export interface InsightsResponse {
  success: boolean;
  insights?: InsightsData;
  error?: string;
}

// Chart Builder Types
export type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'scatter' | 'radar';

export interface ChartConfig {
  xKey: string;
  yKey: string;
  color?: string;
}

export interface ChartData {
  chart_type: ChartType;
  title: string;
  data: Record<string, unknown>[];
  config: ChartConfig;
  query?: string;
}

export interface ChartResponse {
  success: boolean;
  chart?: ChartData;
  error?: string;
}

// Saved Chart (persisted in localStorage)
export interface SavedChart {
  id: string;
  fileKey: string;
  title: string;
  prompt: string;
  chartType: ChartType;
  data: Record<string, unknown>[];
  config: ChartConfig;
  createdAt: string;
  pinned: boolean;
}

// Chat Message (for Talk to Your Data)
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Chat History per file
export interface ChatHistory {
  messages: ChatMessage[];
  suggestedQuestions: string[];
  lastUpdated: string;
}

// Dashboard State (persisted in localStorage)
export interface IntrospectDashboardState {
  selectedFileKey: string | null;
  insights: {
    [fileKey: string]: {
      data: InsightsData;
      fetchedAt: string;
    };
  };
  chatHistory: {
    [fileKey: string]: ChatHistory;
  };
  savedCharts: SavedChart[];
  layout: {
    collapsedSections: string[];
    chartGridColumns: number;
  };
}

// Default state
export const defaultDashboardState: IntrospectDashboardState = {
  selectedFileKey: null,
  insights: {},
  chatHistory: {},
  savedCharts: [],
  layout: {
    collapsedSections: [],
    chartGridColumns: 2,
  },
};

// ============================================================================
// Stats Dashboard Types (Column Distributions for Sparklines)
// ============================================================================

export type VizType = 'numeric' | 'categorical' | 'boolean' | 'high_cardinality' | 'temporal' | 'unknown';

export interface NumericDistribution {
  bins: number[];
  counts: number[];
  min: number | null;
  max: number | null;
  mean: number | null;
  std: number | null;
}

export interface CategoricalDistribution {
  categories: string[];
  counts: number[];
  ratios: number[];
}

export interface BooleanDistribution {
  true_count: number;
  false_count: number;
  null_count: number;
  true_ratio: number;
  false_ratio: number;
  null_ratio: number;
}

export interface HighCardinalityDistribution {
  unique_count: number;
  top_values: string[];
  top_counts: number[];
  coverage: number;
}

export interface TemporalDistribution extends NumericDistribution {
  min_date: string | null;
  max_date: string | null;
}

export interface ColumnDistribution {
  name: string;
  dtype: string;
  viz_type: VizType;
  null_count: number;
  non_null_count: number;
  null_ratio: number;
  distribution: NumericDistribution | CategoricalDistribution | BooleanDistribution | HighCardinalityDistribution | TemporalDistribution | null;
}

export interface DistributionsData {
  file_key: string;
  total_rows: number;
  sampled_rows: number;
  generated_at: string;
  cached: boolean;
  columns: ColumnDistribution[];
}

export interface DistributionsResponse {
  success: boolean;
  distributions?: DistributionsData;
  error?: string;
}
